



















































































import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { DateTime } from "luxon";

// See https://github.com/kaorun343/vue-property-decorator for all options

interface PresetDate {
    label: string;
    value: string;
    start: DateTime;
    end: DateTime;
}

@Component({
    name: "DateRangePicker",
})
export default class DateRangePicker extends Vue {
    DateTime = DateTime;

    @Prop()
    maxWidth: string;

    // Todo: alter date strings, or a preset such as "today" as well
    // Todo: rename to initialValue
    @Prop({ required: true })
    range: DateTime[];
    // initialValue: DateTime[] | string[] | string | undefined;

    internalRange: DateTime[];

    dates: string[] = [];

    menuOpen: boolean = false;

    presetDates: PresetDate[] = [
        {
            label: "Today",
            value: "today",
            start: DateTime.local().startOf("day"),
            end: DateTime.local().endOf("day"),
        },
        {
            label: "Yesterday",
            value: "yesterday",
            start: DateTime.local().minus({ days: 1 }).startOf("day"),
            end: DateTime.local().minus({ days: 1 }).endOf("day"),
        },
        {
            label: "This week",
            value: "thisWeek",
            start: DateTime.local().startOf("week"),
            end: DateTime.local().endOf("week"),
        },
        {
            label: "Last week",
            value: "lastWeek",
            start: DateTime.local().minus({ weeks: 1 }).startOf("week"),
            end: DateTime.local().minus({ weeks: 1 }).endOf("week"),
        },
        {
            label: "This month",
            value: "thisMonth",
            start: DateTime.local().startOf("month"),
            end: DateTime.local().endOf("month"),
        },
        {
            label: "Last month",
            value: "lastMonth",
            start: DateTime.local().minus({ months: 1 }).startOf("month"),
            end: DateTime.local().minus({ months: 1 }).endOf("month"),
        },
    ];

    created() {
        if (this.range.length !== 2)
            throw new Error(
                '<date-ranger-picker> requires exactly 2 luxon date objects to be passed into the "range" prop',
            );

        // Set dates from the luxon objects for vuetify
        this.updateInternalRange();
    }

    selectPreset(preset: PresetDate) {
        this.internalRange = [preset.start, preset.end];
        this.updateVuetifyDates();
        this.menuOpen = false;
    }

    updateVuetifyDates() {
        this.dates = [this.internalRange[0].toISODate(), this.internalRange[1].toISODate()];
    }

    updateInternalRange() {
        this.internalRange = [this.range[0].startOf("day"), this.range[1].endOf("day")];
        this.updateVuetifyDates();
    }

    onStartDateSelected() {
        const startDate = DateTime.fromISO(this.dates[0]).startOf("day");
        let endDate = DateTime.fromISO(this.dates[1]).startOf("day");

        if (startDate > endDate) {
            endDate = startDate;
            this.$tosti.info({
                message: `The end date has been changed to ${endDate.toLocaleString(DateTime.DATE_FULL)}`,
            });
        }

        this.$nextTick(() => this.updateVuetifyDates());
        this.internalRange = [startDate, endDate];
    }

    onEndDateSelected() {
        let startDate = DateTime.fromISO(this.dates[0]).startOf("day");
        const endDate = DateTime.fromISO(this.dates[1]).startOf("day");

        if (endDate < startDate) {
            startDate = endDate;
            this.$tosti.info({
                message: `The start date has been changed to ${startDate.toLocaleString(DateTime.DATE_FULL)}`,
            });
        }

        this.$nextTick(() => this.updateVuetifyDates());
        this.internalRange = [startDate, endDate];
    }

    isPresetActive(preset: PresetDate) {
        return this.internalRange[0].equals(preset.start) && this.internalRange[1].equals(preset.end);
    }

    @Watch("dates", { deep: true })
    updateDates() {
        this.$emit(
            "update:range",
            [DateTime.fromISO(this.dates[0]), DateTime.fromISO(this.dates[1])]
                // If the user picked the end date first, vuetify gives them back in the wrong order, so we sort them
                .sort((d1, d2) => (d1.toSeconds() < d2.toSeconds() ? -1 : 1)),
        );
    }

    // Somehow doesn't update when using as computed (getter)
    inputDisplayValue(): string {
        for (const presetDate of this.presetDates) {
            if (this.isPresetActive(presetDate)) return presetDate.label;
        }

        return [
            this.internalRange[0].toLocaleString(DateTime.DATE_MED),
            this.internalRange[1].toLocaleString(DateTime.DATE_MED),
        ].join(" - ");
    }
}
